import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Sparkles, ShieldAlert, Eye } from "lucide-react";

/**
 * AI CHAT simulation. Used by every AI competency concept.
 * Events: prompt_submitted, prompt_refined, context_added, output_verified,
 * sensitive_data_pasted, ai_use_disclosed, output_accepted_unverified,
 * accepted_off_topic, accepted_biased, accepted_incomplete, vague_prompt,
 * non_disclosure, chose_inappropriate_use, final_decision.
 *
 * The simulator presents a dummy AI response that is concept-specific
 * (driven by scenario data). The candidate must take the right behaviour
 * (verify / refine / disclose / select correct option / etc.) before
 * finalising.
 */
export default function AIChatSim({ scenario, recorder, onFinalize, busy }) {
  const [text, setText] = useState("");
  const [history, setHistory] = useState([]); // {role, content}
  const [submitCount, setSubmitCount] = useState(0);
  const [verified, setVerified] = useState(false);
  const [disclosed, setDisclosed] = useState(false);
  const [chosenOption, setChosenOption] = useState(null);

  const sensitiveText = scenario.sensitive_text_to_avoid || scenario.draft_with_pii || "";
  const expectedKw = (scenario.expected_keywords || []).map((k) => k.toLowerCase());
  const badKw = (scenario.bad_keywords || []).map((k) => k.toLowerCase());
  const claim = scenario.claim;
  const options = scenario.options || [];
  const requiredSections = scenario.required_sections || [];
  const firstPassReturns = scenario.first_pass_returns || [];

  const detectBad = (t) => badKw.some((k) => t.toLowerCase().includes(k));
  const detectExpected = (t) => expectedKw.length === 0 || expectedKw.some((k) => t.toLowerCase().includes(k));
  const detectSensitive = (t) =>
    sensitiveText && (
      t.includes("4111-1111-1111-1111") ||
      t.toLowerCase().includes("01-02-1990") ||
      t.toLowerCase().includes("privet drive") ||
      t.toLowerCase().includes("card 4111")
    );

  const send = () => {
    if (!text.trim()) return;
    const submission = text;
    if (submitCount === 0) recorder.log("prompt_submitted", submission.slice(0, 60));
    else recorder.log("prompt_refined", submission.slice(0, 60));
    if (detectSensitive(submission)) recorder.log("sensitive_data_pasted", submission.slice(0, 30));
    if (!detectExpected(submission)) recorder.log("vague_prompt", submission.slice(0, 30));
    if (submission.toLowerCase().includes("context") || submission.toLowerCase().includes("audience") || submission.toLowerCase().includes("for our")) {
      recorder.log("context_added", "context phrase");
    }
    let response = "Here is a draft based on your prompt.";
    if (claim) response = `Based on a quick search: ${claim} (Confidence: high)`;
    if (firstPassReturns.length && submitCount === 0) response = "Sections produced: " + firstPassReturns.join(", ");
    if (detectBad(submission)) response = "Output is generic and likely off-target.";
    setHistory([...history, { role: "user", content: submission }, { role: "ai", content: response }]);
    setText("");
    setSubmitCount((c) => c + 1);
  };

  const verify = () => { setVerified(true); recorder.log("output_verified"); };
  const disclose = () => { setDisclosed(true); recorder.log("ai_use_disclosed"); };
  const chooseOption = (opt) => {
    setChosenOption(opt);
    if (opt.unsafe) {
      if (opt.discloses === false) recorder.log("non_disclosure", opt.id);
      if (opt.appropriate === false) recorder.log("chose_inappropriate_use", opt.id);
      if (opt.biased) recorder.log("accepted_biased", opt.id);
      if (opt.relevant === false) recorder.log("accepted_off_topic", opt.id);
      if (opt.verified === false) recorder.log("output_accepted_unverified", opt.id);
    }
  };

  const finalize = () => {
    let decision = "submitted";
    if (chosenOption) {
      decision = `selected:${chosenOption.id}`;
    } else if (claim && verified) {
      decision = "rejected_false_claim";
    } else if (sensitiveText) {
      const safe = !history.some((h) => h.role === "user" && detectSensitive(h.content));
      decision = safe ? "anonymised_submission" : "submitted_with_pii";
    } else if (firstPassReturns.length) {
      const ok = submitCount >= 2;
      decision = ok ? "completeness_secured" : "accepted_incomplete";
      if (!ok) recorder.log("accepted_incomplete");
    } else if (submitCount > 0) {
      // generic clarity / refinement / context / framing
      const last = history.findLast?.((h) => h.role === "user")?.content || "";
      if (submitCount > 1) decision = "refined_prompt_submitted";
      else if (detectExpected(last)) decision = "clear_prompt_submitted";
      else decision = "vague_prompt_submitted";
      if (last.toLowerCase().includes("step") || last.toLowerCase().includes("1.")) decision = "task_framed";
      if (last.toLowerCase().includes("context") || last.toLowerCase().includes("for our")) decision = "context_provided";
    }
    onFinalize(decision, { history, verified, disclosed, chosenOption });
  };

  return (
    <div className="grid lg:grid-cols-12 min-h-[480px]" data-testid="ai-chat-sim">
      <div className="lg:col-span-8 p-5 border-b lg:border-b-0 lg:border-r border-[var(--iddl-rule)]">
        <div className="label-mono">Conversation</div>
        <div className="mt-2 paper border h-[280px] overflow-auto p-3 surface" data-testid="ai-chat-history">
          {history.length === 0 && <div className="text-sm text-[var(--iddl-muted)]">No messages yet. Compose your prompt below.</div>}
          {history.map((m, i) => (
            <div key={i} className={`mb-3 ${m.role === "user" ? "" : "pl-4 border-l-2 border-[var(--iddl-rule)]"}`}>
              <div className="label-mono mb-1">{m.role === "user" ? "YOU" : "AI"}</div>
              <div className="text-sm whitespace-pre-wrap">{m.content}</div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            placeholder="Compose a prompt — include task, audience, format, constraints if relevant"
            data-testid="ai-chat-input"
          />
          {sensitiveText && (
            <button
              type="button"
              className="text-[11px] mt-1 underline opacity-70"
              onClick={() => setText(sensitiveText)}
              data-testid="btn-paste-sensitive"
            >
              Paste raw draft (contains customer data)
            </button>
          )}
          <div className="mt-2 flex gap-2">
            <Button onClick={send} className="btn-ink rounded-none" data-testid="btn-ai-send">
              <Sparkles size={12} className="mr-1" /> Send to AI
            </Button>
            <Button onClick={verify} variant="outline" className="rounded-none" data-testid="btn-ai-verify">
              <Eye size={12} className="mr-1" /> {verified ? "Verified" : "Verify output"}
            </Button>
            <Button onClick={disclose} variant="outline" className="rounded-none" data-testid="btn-ai-disclose">
              {disclosed ? "Disclosure marked" : "Mark as AI-assisted"}
            </Button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 p-5">
        <div className="label-mono">Decision</div>
        {options.length > 0 ? (
          <div className="mt-2 grid gap-2">
            {options.map((o) => (
              <button
                key={o.id}
                onClick={() => chooseOption(o)}
                data-testid={`ai-option-${o.id}`}
                className={`text-left p-3 border ${chosenOption?.id === o.id ? "border-[var(--iddl-ink)] bg-[var(--iddl-surface)]" : "border-[var(--iddl-rule)]"}`}
              >
                <div className="text-sm">{o.label}</div>
                {o.unsafe && <div className="label-mono tint-cs border px-1.5 py-0.5 mt-1 inline-flex"><ShieldAlert size={10} className="mr-1" />UNSAFE</div>}
              </button>
            ))}
          </div>
        ) : claim ? (
          <div className="mt-2 grid gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => recorder.log("output_accepted_unverified", claim)} data-testid="btn-accept-claim">
              Accept claim as-is
            </Button>
            <Button className="btn-ink rounded-none" onClick={() => { setChosenOption({ id: "rejected_false_claim" }); recorder.log("output_verified"); }} data-testid="btn-reject-claim">
              Reject claim — not verified
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[var(--iddl-muted)] mt-2">
            Compose, refine and (if needed) verify the AI&apos;s output, then
            finalise.
          </p>
        )}

        <div className="mt-6 border-t border-[var(--iddl-rule)] pt-4">
          <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none w-full" data-testid="btn-finalize-aichat">
            <MessageSquare size={12} className="mr-2" /> Finalise — submit
          </Button>
        </div>
      </div>
    </div>
  );
}
